import React, { useState, useEffect, useRef } from 'react';
import { FileText, Check } from 'lucide-react';

/**
 * Equipment Notes Card Component
 * Large textarea with auto-save functionality
 * Shows character count and last updated timestamp
 */
const EquipmentNotesCard = ({
  notes,
  onSave,
  lastUpdated
}) => {
  const [localNotes, setLocalNotes] = useState(notes || '');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const saveTimeoutRef = useRef(null);

  // FT-531 fix-first (Bug 2 — lost edits on unmount): this card's debounce
  // pattern pre-dates FT-531 and had the same flaw FT-531's new Serial/
  // Model/Category/Location debouncing was built to mirror — typing then
  // leaving the screen within 1000ms silently dropped the edit (cleared the
  // timer instead of flushing it). Fixed here in the same pass so a
  // known-bad pattern isn't knowingly left uncorrected right next to new
  // code that copies it.
  //
  // Always call the LATEST onSave/notes via refs, so a flush (unmount or
  // blur, below) never fires with a prop reference frozen at some earlier
  // render.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const notesRef = useRef(notes);
  notesRef.current = notes;
  // Holds the typed value that WOULD be saved when the pending timer fires,
  // so a flush can save it immediately instead of discarding it. null =
  // nothing pending.
  const pendingRef = useRef(null);

  // Update local state when prop changes
  useEffect(() => {
    setLocalNotes(notes || '');
  }, [notes]);

  // FT-531 fix-first (Bug 2): FLUSH — save a pending edit RIGHT NOW instead
  // of discarding it. Used on unmount and on blur.
  const flush = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (pendingRef.current) {
      const { value } = pendingRef.current;
      pendingRef.current = null;
      if (value !== notesRef.current) {
        setSaving(true);
        Promise.resolve(onSaveRef.current(value))
          .then(() => {
            setJustSaved(true);
            setTimeout(() => setJustSaved(false), 2000);
          })
          .catch(() => {})
          .finally(() => setSaving(false));
      }
    }
  };

  // Debounced auto-save
  const handleNotesChange = (e) => {
    const newValue = e.target.value;
    setLocalNotes(newValue);
    pendingRef.current = { value: newValue };

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for auto-save
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      flush();
    }, 1000); // 1 second debounce
  };

  // FT-531 fix-first (Bug 2): flush (not merely clear) on unmount.
  useEffect(() => {
    return () => {
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const characterCount = localNotes.length;
  const characterLimit = 2000;
  const isNearLimit = characterCount > characterLimit * 0.8;
  const isOverLimit = characterCount > characterLimit;

  return (
    <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden mb-6">
      <div className="bg-slate-700 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-white" />
            <h3 className="text-base font-semibold text-white">Equipment Notes</h3>
          </div>
          {saving && (
            <span className="text-xs text-slate-200 flex items-center gap-1">
              <span className="inline-block h-3 w-3 border-2 border-slate-400 border-t-white rounded-full animate-spin"></span>
              Saving...
            </span>
          )}
          {justSaved && !saving && (
            <span className="text-xs text-green-300 flex items-center gap-1">
              <Check className="h-3 w-3" />
              Saved
            </span>
          )}
        </div>
      </div>
      <div className="px-4 py-4">
        <p className="text-xs text-gray-600 mb-3">
          Add notes about suppliers, servicing preferences, location details, or any other relevant information
        </p>

        <textarea
          value={localNotes}
          onChange={handleNotesChange}
          onBlur={flush}
          placeholder="Add notes about this equipment... (e.g., purchased from ABC Marine Ltd, prefer XYZ Services for annual checks, difficult to access in rough weather)"
          className={`w-full px-3 py-2 border rounded-md text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600 resize-none ${
            isOverLimit ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
          }`}
          rows={6}
        />

        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-gray-500">
            {lastUpdated && (
              <span>Last updated: {formatTimestamp(lastUpdated)}</span>
            )}
          </div>
          <div className={`text-xs ${isOverLimit ? 'text-red-600 font-medium' : isNearLimit ? 'text-amber-600' : 'text-gray-500'}`}>
            {characterCount} / {characterLimit} characters
          </div>
        </div>

        {isOverLimit && (
          <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
            <span>⚠️ Character limit exceeded. Consider shortening your notes.</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default EquipmentNotesCard;
